// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getToken } from '../api/auth';
import { useCurrency, CURRENCIES } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NAV_ITEMS = [
  { name: 'Home',         path: '/' },
  { name: 'Collections',  path: '/collections' },
  { name: 'Hot Deals',    path: '/deals' },
  { name: 'Shop by Reel', path: '/reels' },
  { name: 'About Us',     path: '/about' },
  { name: 'Contact Us',   path: '/contact' },
];

const TICKER = [
  'Free Shipping on Orders Above ₹1499 (India)', '✦',
  'Free Gift on Orders Above ₹2999', '✦',
  'Orders Above ₹3999 — Free Gift + ₹150 Off', '✦',
  'Orders Above ₹6999 — Free Gift + ₹300 Off', '✦',
  'Orders Above ₹9999 — Free Gift + ₹500 Off', '✦',
];

const doubled = [...TICKER, ...TICKER];

const Navbar = () => {
  const [isOpen, setIsOpen]       = useState(false);
  const [scrolled, setScrolled]   = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [currOpen, setCurrOpen]   = useState(false);
  const currRef                   = useRef(null);
  const location                  = useLocation();

  const { currency, setCurrency, activeCurrency } = useCurrency();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  useEffect(() => {
    const fn = () => { if (window.innerWidth > 860) setIsOpen(false); };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (currRef.current && !currRef.current.contains(e.target)) setCurrOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

    useEffect(() => {
      const fetchCartCount = async () => {
        if (!getToken()) { setCartCount(0); return; }
        try {
          const res  = await fetch(`${API}/user/cart`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const data = await res.json();
          if (data.success) {
            const total = data.cart.reduce((sum, item) => sum + (item.qty || 1), 0);
            setCartCount(total);
          }
        } catch { setCartCount(0); }
      };

      fetchCartCount();

      // ✅ ADD THIS — listen for cart changes from anywhere
      window.addEventListener('cart-updated', fetchCartCount);
      return () => window.removeEventListener('cart-updated', fetchCartCount);

    }, [location.pathname]); // keep this dependency too

  const isLoggedIn = !!getToken();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Jost:wght@400;500;600&display=swap');

        .nb { position: fixed; top: 0; left: 0; right: 0; z-index: 9999; font-family: 'Jost', sans-serif; display: flex; flex-direction: column; }
        .nb.scrolled { box-shadow: 0 4px 24px rgba(0,0,0,0.10); }

        /* ── STRIP ── */
        .nb-strip { background: #B08D57; overflow: hidden; height: 36px; display: flex; align-items: center; flex-shrink: 0; }
        .nb-strip-track { display: inline-flex; align-items: center; white-space: nowrap; will-change: transform; }
        .nb-strip-item { font-size: 11px; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; color: #fff; padding: 0 28px; flex-shrink: 0; }

        /* ── LOGO ROW ── */
        .nb-logo {
          background: #fff;
          display: flex;
          align-items: center;
          padding: 0 40px;
          height: 82px;
          flex-shrink: 0;
          border-bottom: 1px solid #f0ebe3;
          position: relative;
        }

        /* LEFT slot */
        .nb-left {
          display: flex;
          align-items: center;
          flex: 1;
        }

        /* Logo centred absolutely */
        .nb-logo-center {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .nb-logo-center a { display: block; text-decoration: none; pointer-events: all; }
        .nb-logo-center img { height: 54px; width: auto; display: block; }

        /* RIGHT icons */
        .nb-icons {
          display: flex;
          align-items: center;
          gap: 2px;
          flex: 1;
          justify-content: flex-end;
        }

        /* Burger */
        .nb-burger {
          display: none;
          background: none;
          border: 1px solid #d4c5a9;
          width: 36px; height: 36px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 17px;
          color: #5a5248;
          align-items: center;
          justify-content: center;
          line-height: 1;
          transition: border-color 0.2s, color 0.2s;
          flex-shrink: 0;
        }
        .nb-burger:hover { border-color: #B08D57; color: #B08D57; }

        /* ── ICON BTN ── */
        .nb-icon-btn {
          background: none; border: none; outline: none; cursor: pointer;
          color: #5a5248;
          display: flex; align-items: center; justify-content: center;
          width: 40px; height: 40px;
          border-radius: 4px;
          text-decoration: none;
          transition: color 0.2s;
          position: relative;
          flex-shrink: 0;
        }
        .nb-icon-btn:hover { color: #B08D57; }
        .nb-icon-btn:focus { outline: none; }
        .nb-icon-btn:focus-visible { outline: 2px solid #B08D57; outline-offset: 2px; }

        /* ── CART BADGE ── */
        .nb-cart-badge {
          position: absolute; top: 3px; right: 3px;
          min-width: 16px; height: 16px;
          border-radius: 50%;
          background: #B08D57; color: #fff;
          font-size: 9px; font-weight: 700; font-family: 'Jost', sans-serif;
          display: flex; align-items: center; justify-content: center;
          padding: 0 3px; line-height: 1;
          pointer-events: none;
          border: 1.5px solid #fff;
        }
        .nb-icon-btn:hover .nb-cart-badge { background: #1C1A18; }

        /* ── CURRENCY SELECTOR ── */
        .nb-curr-wrap { position: relative; display: flex; align-items: center; margin-left: 4px; }
        .nb-curr-btn {
          display: flex; align-items: center; gap: 5px;
          background: none;
          border: 1.5px solid #d4c5a9;
          border-radius: 4px;
          padding: 0 10px;
          height: 34px;
          cursor: pointer;
          font-family: 'Jost', sans-serif;
          font-size: 11px; font-weight: 600;
          color: #3a3530;
          letter-spacing: 0.06em;
          transition: border-color 0.2s, color 0.2s;
          white-space: nowrap;
        }
        .nb-curr-btn:hover { border-color: #B08D57; color: #B08D57; }
        .nb-curr-flag { font-size: 15px; line-height: 1; display: flex; align-items: center; }
        .nb-curr-code { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; }
        .nb-curr-arrow { font-size: 7px; color: #9a8e82; transition: transform 0.2s; margin-left: 1px; line-height: 1; }
        .nb-curr-arrow.open { transform: rotate(180deg); }

        /* ── CURRENCY DROPDOWN ── */
        .nb-curr-drop {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: #fff;
          border: 1px solid #e8e0d4;
          border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.13);
          min-width: 168px;
          z-index: 9999;
          overflow: hidden;
        }
        .nb-curr-drop-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 14px;
          font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 500;
          color: #3a3530;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          border: none; background: none; width: 100%; text-align: left;
        }
        .nb-curr-drop-item:not(:last-child) { border-bottom: 1px solid #f5f0e8; }
        .nb-curr-drop-item:hover { background: #fdf9f4; color: #B08D57; }
        .nb-curr-drop-item.active { background: #f5f0e8; color: #B08D57; font-weight: 600; }
        .nb-curr-drop-flag { font-size: 17px; line-height: 1; }
        .nb-curr-drop-code { font-weight: 600; letter-spacing: 0.06em; min-width: 32px; }
        .nb-curr-drop-sym { font-size: 11px; color: #9a8e82; flex: 1; }
        .nb-curr-drop-check { margin-left: auto; color: #B08D57; font-size: 11px; }

        /* ── NAV LINKS (desktop) ── */
        .nb-nav { background: #fff; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px; flex-shrink: 0; border-bottom: 1px solid #f0ebe3; }
        .nb-nav a { position: relative; font-size: 11.5px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #3a3530; text-decoration: none; padding: 13px 20px; white-space: nowrap; transition: color 0.2s; }
        .nb-nav a:hover { color: #B08D57; }
        .nb-nav a.active { color: #B08D57; }
        .nb-nav a::after { content: ''; position: absolute; bottom: 0; left: 50%; right: 50%; height: 2px; background: #B08D57; transition: left 0.25s, right 0.25s; border-radius: 2px 2px 0 0; }
        .nb-nav a:hover::after, .nb-nav a.active::after { left: 14px; right: 14px; }

        /* ── DRAWER ── */
        .nb-drawer { background: #fff; border-top: 2px solid #B08D57; overflow: hidden; flex-shrink: 0; }
        .nb-drawer-link {
          display: flex; align-items: center; justify-content: space-between;
          padding: 15px 28px;
          font-size: 11.5px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase;
          color: #3a3530; text-decoration: none;
          border-bottom: 1px solid #f5f0e8;
          transition: color 0.2s, background 0.2s;
        }
        .nb-drawer-link:last-child { border-bottom: none; }
        .nb-drawer-link:hover, .nb-drawer-link.active { color: #B08D57; background: #fdf9f4; }
        .nb-arr { color: #c8b99a; font-size: 14px; transition: transform 0.2s, color 0.2s; }
        .nb-drawer-link:hover .nb-arr { transform: translateX(4px); color: #B08D57; }

        /* Drawer icon row — login/account */
        .nb-drawer-toprow {
          display: flex;
          align-items: center;
          gap: 0;
          padding: 14px 20px;
          border-bottom: 1px solid #f5f0e8;
          background: #fdf9f4;
        }
        .nb-drawer-action {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          text-decoration: none;
          color: #3a3530;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 6px 8px;
          border-radius: 4px;
          transition: color 0.2s, background 0.2s;
        }
        .nb-drawer-action:hover { color: #B08D57; }
        .nb-drawer-action svg { flex-shrink: 0; }
        .nb-drawer-action-divider {
          width: 1px; height: 28px;
          background: #e8e0d4;
          flex-shrink: 0;
          margin: 0 4px;
        }

        /* Mobile currency row */
        .nb-drawer-curr { display: flex; align-items: center; gap: 6px; padding: 12px 20px; border-bottom: 1px solid #f5f0e8; flex-wrap: wrap; }
        .nb-drawer-curr-label { font-size: 9px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: #9a8e82; flex-basis: 100%; margin-bottom: 6px; }
        .nb-drawer-curr-opt { display: flex; align-items: center; gap: 4px; padding: 5px 9px; border: 1px solid #e0d5c5; border-radius: 3px; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 600; color: #3a3530; background: none; cursor: pointer; transition: all 0.15s; }
        .nb-drawer-curr-opt.active { background: #B08D57; color: #fff; border-color: #B08D57; }
        .nb-drawer-curr-opt:not(.active):hover { border-color: #B08D57; color: #B08D57; }

        /* ── RESPONSIVE ── */
        @media (max-width: 860px) {
          .nb-nav { display: none; }
          .nb-burger { display: flex; }

          .nb-logo { padding: 0 14px; height: 64px; }
          .nb-left { flex: 0 0 auto; }
          .nb-logo-center img { height: 38px; }

          /* Account icon hidden on mobile — moved to drawer */
          .nb-icon-account { display: none; }

          .nb-icon-btn { width: 32px; height: 32px; }
          .nb-icons { gap: 0; }

          .nb-curr-btn { height: 28px; padding: 0 7px; font-size: 10px; gap: 3px; }
          .nb-curr-flag { font-size: 12px; }
          .nb-curr-code { font-size: 10px; }
          .nb-curr-arrow { font-size: 6px; }

          .nb-strip { height: 42px; }
          .nb-strip-item { font-size: 10px; padding: 0 18px; letter-spacing: 0.12em; }
        }
      `}</style>

      <div className={`nb${scrolled ? ' scrolled' : ''}`}>

        {/* 1 — GOLD STRIP */}
        <div className="nb-strip">
          <motion.div
            className="nb-strip-track"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}
          >
            {doubled.map((t, i) => (
              <span key={i} className="nb-strip-item">{t}</span>
            ))}
          </motion.div>
        </div>

        {/* 2 — LOGO ROW */}
        <div className="nb-logo">

          {/* LEFT: Burger */}
          <div className="nb-left">
            <button
              className="nb-burger"
              onClick={() => setIsOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {isOpen ? '✕' : '☰'}
            </button>
          </div>

          {/* CENTER: Logo */}
          <div className="nb-logo-center">
            <Link to="/">
              <motion.img
                src="/logo.png"
                alt="Aamira"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </Link>
          </div>

          {/* RIGHT: Icons */}
          <div className="nb-icons">

            {/* Search — visible on ALL screen sizes */}
            <Link to="/products" className="nb-icon-btn" aria-label="Search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </Link>

            {/* Account — hidden on mobile (shown in drawer instead) */}
            <Link to="/account" className="nb-icon-btn nb-icon-account" aria-label="Account">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </Link>

            {/* Cart */}
            <Link
              to={isLoggedIn ? '/cart' : '/account'}
              className="nb-icon-btn"
              aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              {cartCount > 0 && (
                <motion.span
                  className="nb-cart-badge"
                  key={cartCount}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {cartCount > 99 ? '99+' : cartCount}
                </motion.span>
              )}
            </Link>

            {/* Currency */}
            <div className="nb-curr-wrap" ref={currRef}>
              <button
                className="nb-curr-btn"
                onClick={() => setCurrOpen(o => !o)}
                aria-label="Select currency"
              >
                <span className="nb-curr-flag">{activeCurrency.flag}</span>
                <span className="nb-curr-code">{activeCurrency.code}</span>
                <span className={`nb-curr-arrow${currOpen ? ' open' : ''}`}>▼</span>
              </button>

              <AnimatePresence>
                {currOpen && (
                  <motion.div
                    className="nb-curr-drop"
                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.18 }}
                  >
                    {CURRENCIES.map(c => (
                      <button
                        key={c.code}
                        className={`nb-curr-drop-item${currency === c.code ? ' active' : ''}`}
                        onClick={() => { setCurrency(c.code); setCurrOpen(false); }}
                      >
                        <span className="nb-curr-drop-flag">{c.flag}</span>
                        <span className="nb-curr-drop-code">{c.code}</span>
                        <span className="nb-curr-drop-sym">{c.symbol}</span>
                        {currency === c.code && <span className="nb-curr-drop-check">✓</span>}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </div>

        {/* 3 — NAV LINKS (desktop only) */}
        <nav className="nb-nav">
          {NAV_ITEMS.map(item => (
            <Link
              key={item.name}
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* 4 — MOBILE DRAWER */}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              className="nb-drawer"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* ── Login / Account row at top of drawer ── */}
              <div className="nb-drawer-toprow">
                <Link
                  to="/account"
                  className="nb-drawer-action"
                  onClick={() => setIsOpen(false)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {isLoggedIn ? 'My Account' : 'Login / Register'}
                </Link>

                <div className="nb-drawer-action-divider" />

                <Link
                  to={isLoggedIn ? '/cart' : '/account'}
                  className="nb-drawer-action"
                  onClick={() => setIsOpen(false)}
                  style={{ flex: 'none', paddingRight: 0 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <path d="M16 10a4 4 0 0 1-8 0"/>
                  </svg>
                  My Cart
                  {cartCount > 0 && (
                    <span style={{
                      background: '#B08D57', color: '#fff',
                      fontSize: '9px', fontWeight: 700,
                      borderRadius: '50%', minWidth: '16px', height: '16px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', marginLeft: '2px'
                    }}>
                      {cartCount}
                    </span>
                  )}
                </Link>
              </div>

              {/* Currency row */}
              <div className="nb-drawer-curr">
                <span className="nb-drawer-curr-label">Select Currency</span>
                {CURRENCIES.map(c => (
                  <button
                    key={c.code}
                    className={`nb-drawer-curr-opt${currency === c.code ? ' active' : ''}`}
                    onClick={() => setCurrency(c.code)}
                  >
                    <span>{c.flag}</span>
                    <span>{c.code}</span>
                  </button>
                ))}
              </div>

              {/* Nav links */}
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={item.path}
                    className={`nb-drawer-link${location.pathname === item.path ? ' active' : ''}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                    <span className="nb-arr">→</span>
                  </Link>
                </motion.div>
              ))}

            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default Navbar;